import cn from "classnames";
import { useEffect, useState, useCallback, useRef } from "react";

import { Icon } from "@trussworks/react-uswds";

// components
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// utils
import { saveErrorToast } from "utils/edit-profile";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// styles
import styles from "../ServiceSection.module.scss";
import { Button } from "components/LibraryComponents/Components";

const MAX_KEYWORDS_CHARACTERS = 750;

export default function KeywordSection({
  setUnsavedChanges,
  saveConfirmation,
  section
}: {
  setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>,
  saveConfirmation: ReturnType<typeof useSaveConfirmation>,
  section: string
}) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [keywords, setKeywords] = useState<string[]>([]);
  const [value, setValue] = useState<string>("");
  const [showKeywordsLimitError, setShowKeywordsLimitError] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = saveConfirmation

  const addTask = useMeiliTaskToast();

  const keywordInputRef = useRef<HTMLInputElement>(null);
  const isInitialized = useRef(false); // Track if keywords have been initialized

  const totalKeywordCharacters = keywords.join(", ").length;

  // Check if keywords have changed
  const hasFieldChanged = useCallback(
    () => keywords.join(", ") !== extendedProfile?.keywords,
    [keywords, extendedProfile]
  );

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(hasFieldChanged());
    clearConfirmation()
    return () => {
      setUnsavedChanges(false);
    };
  }, [keywords, setUnsavedChanges]);

  // Initialize keywords from extendedProfile
  useEffect(() => {
    if (
      extendedProfile &&
      !isInitialized.current &&
      keywords.length === 0 &&
      extendedProfile?.keywords
    ) {
      const initial = extendedProfile.keywords.split(",").map((k) => k.trim());
      setKeywords(initial);

      isInitialized.current = true;
    }
  }, [extendedProfile]);

  const handleParentClick = () => {
    if (keywordInputRef.current) {
      keywordInputRef.current.focus();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value === ",") return;

    const totalCount = totalKeywordCharacters + value.length + 1;

    if (totalCount <= MAX_KEYWORDS_CHARACTERS) {
      const keywordValue = value.replace(/,+/g, "");
      setValue(keywordValue);

      if (showKeywordsLimitError) {
        setShowKeywordsLimitError(false);
      }
    } else {
      setShowKeywordsLimitError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ",") {
      handleAddKeyword();
    }
  };

  const handleAddKeyword = () => {
    const totalCount = totalKeywordCharacters + value.length + 1; // +1 for comma

    if (totalCount <= MAX_KEYWORDS_CHARACTERS) {
      if (value.trim() !== "") {
        setKeywords([...keywords, value.trim()]);
        setValue("");
      }
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));

    if (showKeywordsLimitError) {
      setShowKeywordsLimitError(false);
    }
  };

  const handleSaveField = async () => {
    if (!hasFieldChanged() || isSaving) return;

    setIsSaving(true);

    try {
      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          keywords: keywords.join(", "),
        })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        keywords: keywords.join(", "),
      });

      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: section,
        status: responseData?.status,
      });
      showConfirmation(section);
    } catch (error: any) {
      console.error(`Error saving keywords:`, error);
      saveErrorToast();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(styles["section-content"], styles["space-y-6"])}>
      <div>
        <h3 className={styles["title"]}>Keywords</h3>
        <p className={styles["desc"]}>
          Type comma-separated keywords that describe your small business to
          help it show up in search results.
        </p>
      </div>

      <div>
        <div
          className={cn(styles["keywords-controller"], {
            [styles["keywords-limit-error"]]: showKeywordsLimitError,
          })}
          onClick={handleParentClick}
        >
          {keywords.map((keyword, index) => (
            <div
              role="button"
              tabIndex={-1}
              key={index}
              data-tag-index={index}
              className={styles["keyword-tag"]}
            >
              <span className={styles["tag-text"]}>{keyword}</span>
              <span
                className={styles["tag-icon"]}
                onClick={() => handleRemoveKeyword(index)}
              >
                <Icon.Close />
              </span>
            </div>
          ))}
          <input
            ref={keywordInputRef}
            type="text"
            placeholder="Type one or more keywords"
            className={styles["keyword-input"]}
            aria-invalid="false"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded="false"
            autoCapitalize="none"
            spellCheck="false"
            role="combobox"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        </div>

        {showKeywordsLimitError && (
          <span className={styles["keywords-limit-error-text"]}>
            <strong>Keywords must be 750 characters or less.</strong> Remove
            existing keywords before adding more.
          </span>
        )}
      </div>

      <ResponsiveStack>
        <Button
          type="button"
          loading={isSaving}
          disabled={!hasFieldChanged() || isSaving || keywords.length === 0 || totalKeywordCharacters > MAX_KEYWORDS_CHARACTERS}
          onClick={handleSaveField}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
        {lastSavedSection === section && <Saved />}
      </ResponsiveStack>
    </div>
  );
}
